import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  useMediaQuery,
  CircularProgress,
  Modal,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import LogsIcon from "@mui/icons-material/Article";
import { FetchDeployments } from "../Api";

interface Project {
  id: number;
  uid: string;
  device_id: string;
  status: string;
  error: string;
  timestamp: string;
  completed_at: string;
}

const ProjectsList: React.FC = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [selectedLogs, setSelectedLogs] = useState<string | null>(null);
  const [logs, setLogs] = useState<any[]>([]);
  const logsEndRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const loadProjects = async () => {
      try {
        const data = await FetchDeployments("mqttapi/v1/fetch-deployment");
        if (data == null) {
          setProjects([]);
        }
        setProjects(data);
        // console.log(data);
        setLoading(false);
      } catch (err) {
        setError("Failed to fetch projects.");
        setLoading(false);
      }
    };
    const interval = setInterval(() => {
      loadProjects();
    }, 5000);

    // Cleanup interval on component unmount
    return () => clearInterval(interval);
  }, []);
  useEffect(() => {
    // Initialize the WebSocket connection
    const protocol = window.location.protocol === "https:" ? "wss" : "ws";
    const host = window.location.host; // includes hostname and port (if any)
    const ws = new WebSocket(`${protocol}://${host}/ws`);

    // Set up event listeners
    ws.onopen = () => {
      console.log("WebSocket connection opened");
    };

    ws.onmessage = (event) => {
      // console.log("Message received:", event.data);

      // Update logs state
      setLogs((prevLogs) => [...prevLogs, event.data]);
    };

    ws.onclose = () => {
      console.log("WebSocket connection closed");
    };

    ws.onerror = (error) => {
      console.error("WebSocket error:", error);
    };

    // Cleanup on unmount
    return () => {
      ws.close();
    };
  }, []);
  useEffect(() => {
    // Auto-scroll to the bottom whenever logs change
    if (logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [logs]);
  const handleLogsClick = () => {
    // setSelectedLogs(logs);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedLogs(null);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" sx={{ textAlign: "center", mt: 2 }}>
        {error}
      </Typography>
    );
  }
  const calculateDuration = (
    startTimestamp: string,
    completionTimestamp: string
  ) => {
    const startTime = new Date(startTimestamp);
    const completionTime = new Date(completionTimestamp);

    // Calculate the difference in milliseconds
    const durationMs = completionTime.getTime() - startTime.getTime();

    // Convert milliseconds to a readable format
    const seconds = Math.floor((durationMs / 1000) % 60);
    const minutes = Math.floor((durationMs / (1000 * 60)) % 60);
    const hours = Math.floor((durationMs / (1000 * 60 * 60)) % 24);
    const days = Math.floor(durationMs / (1000 * 60 * 60 * 24));

    return `${days > 0 ? `${days}d ` : ""}${hours > 0 ? `${hours}h ` : ""}${
      minutes > 0 ? `${minutes}m ` : ""
    }${seconds}s`;
  };
  return (
    <Box sx={{ fontFamily: "Arial, sans-serif", p: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
        }}
      >
        <Typography variant="h5" sx={{ margin: 0 }}>
          Projects List
        </Typography>
        <Button
          variant="contained"
          color="primary"
          size={isSmallScreen ? "small" : "medium"}
          startIcon={<LogsIcon />}
          onClick={() => handleLogsClick()}
        >
          Logs
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table
          sx={{ minWidth: 650 }}
          aria-label="projects table"
          size={isSmallScreen ? "small" : "medium"}
        >
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>UID</strong>
              </TableCell>
              <TableCell>
                <strong>Device ID</strong>
              </TableCell>
              <TableCell>
                <strong>Status</strong>
              </TableCell>
              <TableCell>
                <strong>Started At</strong>
              </TableCell>
              <TableCell>
                <strong>Completed At</strong>
              </TableCell>
              <TableCell>
                <strong>Error</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {projects?.map((project) => (
              <TableRow
                key={project.uid}
                sx={{
                  "&:last-child td, &:last-child th": { border: 0 },
                  "&:hover": { backgroundColor: theme.palette.action.hover },
                }}
              >
                <TableCell component="th" scope="row">
                  {project.uid}
                </TableCell>
                <TableCell>{project.device_id}</TableCell>
                <TableCell>{project.status}</TableCell>
                <TableCell>{project.timestamp}</TableCell>
                <TableCell>
                  {calculateDuration(project.timestamp, project.completed_at)}
                </TableCell>
                <TableCell>{project.error}</TableCell>
              </TableRow>
            ))}
            {projects?.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No projects available.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog
        maxWidth="xl"
        fullWidth
        open={modalOpen}
        onClose={handleCloseModal}
      >
        <DialogTitle display={"flex"} justifyContent={"space-between"}>
          <>Logs </>
          <Button variant="outlined" onClick={handleCloseModal}>
            Close
          </Button>
        </DialogTitle>
        <DialogContent
          sx={{
            overflowY: "auto", // Enable scrolling
          }}
        >
          {logs.map((log, index) => (
            <Typography
              key={index}
              id="logs-modal-description"
              sx={{ wordWrap: "break-word", mb: 1 }}
            >
              {log}
            </Typography>
          ))}
          <div ref={logsEndRef} />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ProjectsList;
